import * as React from 'react';
import {
  GetInvitationDetailsLazyQueryHookResult,
  GetInvitationDetailsQuery,
  JoinOrganizationFromInvitationMutationFn,
  RegisterEmailAvailableLazyQueryHookResult
} from '../../../models/graphql';
import { GetMe_me } from '../../../models/legacy-graphql';
import { useEffect, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { IInvitation } from '../../../models/auth';
import { JoinOrganization } from '../JoinOrganization';
import { InviteView, InviteViewProps } from './Invite.view';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { useAuth } from '../../../hooks/useAuth';
import { changeSelectedWorkspaceId, invitationDetailsVar } from '../../../providers/AppProvider';

export type JoinOrganizationType = (token: string) => void;

export type InviteControllerProps = {
  error: string | JSX.Element;
  getInvitationDetails: GetInvitationDetailsLazyQueryHookResult[0];
  inviteCalled: boolean;
  joinOrganizationFromInvitation: JoinOrganizationFromInvitationMutationFn;
  lazyRegisterEmailAvailable: RegisterEmailAvailableLazyQueryHookResult[0];
  registerEmailCalled: boolean;
  submitted: boolean;
  userData: GetMe_me | undefined;
};

export type InviteRouteParams = {
  token: string;
};

export const InviteController = (props: InviteControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { token } = useParams() as InviteRouteParams;
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const invitationDetails = useReactiveVar(invitationDetailsVar);
  const calledJoinRef = useRef<boolean>(false);
  const [error, setError] = useState<JSX.Element | string>(props.error);

  const setInvitationDetails = (
    invitationDetails: GetInvitationDetailsQuery['getInvitationDetails'],
    token: string
  ): void => {
    const { organizationName, organizationID, email } = invitationDetails;
    const invitationDetailsLocal: IInvitation = {
      token,
      organizationID,
      organizationName,
      invitationEmail: email
    };
    invitationDetailsVar(invitationDetailsLocal);
  };

  const joinOrganization: JoinOrganizationType = (invitationToken: string) => {
    props
      .joinOrganizationFromInvitation({
        variables: {
          invitationToken
        }
      })
      .then(result => {
        if (result.data?.joinOrganizationFromInvitation) {
          sessionStorage.removeItem('invitation');
          const workspaceId = result.data.joinOrganizationFromInvitation.workspaces[0]?.id;
          changeSelectedWorkspaceId(workspaceId, '/');
        }
      })
      .catch(() => {
        // Prevent runtime error - the actual error is rendered in the view.
      });
  };

  //check if component has just been mounted and call getDetails
  useEffect(() => {
    if (!props.inviteCalled) {
      props
        .getInvitationDetails({ variables: { invitationToken: token } })
        .then(result => {
          const { data, error } = result;
          if (data) {
            setInvitationDetails(data.getInvitationDetails, token);
          }
          if (error) {
            throw error;
            //TODO is should be temporary. once the issue is fixed we should remove. https://github.com/apollographql/apollo-client/issues/9142
          }
        })
        .catch(e => {
          setError(remapApolloErrors(e));
        });
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [props.inviteCalled]);

  // trigger when we receive all the props, and invitationDetails reactiveVar has been set.
  // We check if we should call joinOrganizationFromInvitation for different cases
  useEffect(() => {
    // checks if user is logged in && and userEmail = invited email && join has not been called
    if (
      !calledJoinRef.current &&
      isLoggedIn &&
      invitationDetails?.invitationEmail &&
      props.userData?.email &&
      invitationDetails?.invitationEmail === props.userData.email &&
      !(error || props.error) &&
      !props.submitted
    ) {
      ///
      joinOrganization(token);
      calledJoinRef.current = true;
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [calledJoinRef, invitationDetails]);

  useEffect(() => {
    // check if user is not logged and redirect to signup or login
    if (
      !isLoggedIn &&
      invitationDetails?.invitationEmail &&
      !props.submitted &&
      !(error || props.error) &&
      !props.registerEmailCalled
    ) {
      props
        .lazyRegisterEmailAvailable({
          variables: {
            email: invitationDetails.invitationEmail
          }
        })
        .then(result => {
          const { data, error } = result;
          if (data?.registerEmailAvailable) {
            // no email in the database -> redirect to signup
            navigate({ pathname: '/signup' });
          } else if (data && !data.registerEmailAvailable) {
            // already exist, force login
            navigate({ pathname: '/login' });
          }
          if (error) {
            throw error;
            //TODO is should be temporary. once the issue is fixed we should remove. https://github.com/apollographql/apollo-client/issues/9142
          }
        })
        .catch(e => {
          setError(remapApolloErrors(e));
        });
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [isLoggedIn, invitationDetails, props.submitted, error, props.error, props.registerEmailCalled]);

  // check is user logged in and userEmail != invited email, redirect to join component
  if (
    isLoggedIn &&
    invitationDetails?.invitationEmail &&
    props.userData?.email &&
    invitationDetails.invitationEmail !== props.userData.email &&
    !(error || props.error) &&
    !props.submitted
  ) {
    //
    const info = (
      <>
        You have been invited to join {invitationDetails?.organizationName} as{' '}
        <a>{invitationDetails?.invitationEmail}</a>, but you are currently logged in as <a>{props.userData?.email}</a>.
        How would you like to proceed?
      </>
    );

    return (
      <JoinOrganization
        email={props.userData.email}
        info={info}
        error={props.error}
        joinAction={joinOrganization}
        token={token}
      />
    );
  }

  const InviteViewProps: InviteViewProps = {
    error: error || props.error
  };

  return <InviteView {...InviteViewProps} />;
};
